import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Auth screens
import Login from "./screens/auth/Login";
import AcceptInvite from "./screens/auth/AcceptInvite";

// Owner screens
import OwnerLayout from "./screens/owner/OwnerLayout";
import JobInbox from "./screens/owner/JobInbox";
import LiveJobs from "./screens/owner/LiveJobs";
import ReadyToBill from "./screens/owner/ReadyToBill";
import Invoices from "./screens/owner/Invoices";
import Financials from "./screens/owner/Financials";
import Locations from "./screens/owner/Locations";
import AccountReps from "./screens/owner/AccountReps";
import Guards from "./screens/owner/Guards";
import RateCards from "./screens/owner/RateCards";
import Settings from "./screens/owner/Settings";
import Dashboard from "./screens/owner/Dashboard";
import EmailIntake from "./screens/owner/EmailIntake";

// Guard screens
import GuardLayout from "./screens/guard/GuardLayout";
import JobPool from "./screens/guard/JobPool";
import MyJobs from "./screens/guard/MyJobs";
import JobExecution from "./screens/guard/JobExecution";
import GuardEarnings from "./screens/guard/GuardEarnings";
import GuardProfile from "./screens/guard/GuardProfile";

// Route guards
function OwnerRoute({ children }) {
  const { currentUser, isOwner } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  if (!isOwner) return <Navigate to="/guard" />;
  return children;
}

function GuardRoute({ children }) {
  const { currentUser, isGuard, isOwner } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  if (!isGuard && !isOwner) return <Navigate to="/login" />;
  return children;
}

function RootRedirect() {
  const { currentUser, isOwner, isGuard, userProfile } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  // Owners can default to the guard view (Settings → Login view).
  if (isOwner) return <Navigate to={userProfile?.defaultView === "guard" ? "/guard/pool" : "/owner/dashboard"} />;
  if (isGuard) return <Navigate to="/guard/pool" />;
  return <Navigate to="/login" />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Root */}
      <Route path="/" element={<RootRedirect />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:inviteId" element={<AcceptInvite />} />

      {/* Owner */}
      <Route path="/owner" element={<OwnerRoute><OwnerLayout /></OwnerRoute>}>
        <Route index element={<Navigate to="/owner/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="inbox" element={<JobInbox />} />
        <Route path="intake" element={<EmailIntake />} />
        <Route path="live" element={<LiveJobs />} />
        <Route path="billing" element={<ReadyToBill />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="financials" element={<Financials />} />
        <Route path="locations" element={<Locations />} />
        <Route path="reps" element={<AccountReps />} />
        <Route path="guards" element={<Guards />} />
        <Route path="rates" element={<RateCards />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Guard */}
      <Route path="/guard" element={<GuardRoute><GuardLayout /></GuardRoute>}>
        <Route index element={<Navigate to="/guard/pool" />} />
        <Route path="pool" element={<JobPool />} />
        <Route path="jobs" element={<MyJobs />} />
        <Route path="job/:jobId" element={<JobExecution />} />
        <Route path="earnings" element={<GuardEarnings />} />
        <Route path="profile" element={<GuardProfile />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}
